import { UNDERLINE_STATIC } from "./link-underline"
import { type ComponentPropsWithoutRef, type ReactElement } from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { useReducedMotionFallback } from "@hooks/useReducedMotionFallback"
import { CloseIcon } from "@/react/components/icons/CloseIcon"
import { SuccessCircleIcon } from "@/react/components/icons/SuccessCircleIcon"
import { ErrorCircleIcon } from "@/react/components/icons/ErrorCircleIcon"
import { WarningIcon } from "@/react/components/icons/WarningIcon"
import { InfoIcon } from "@/react/components/icons/InfoIcon"

const ToastProvider = ToastPrimitives.Provider

function ToastViewport({ className, ...props }: ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>) {
  return (
    <ToastPrimitives.Viewport
      className={cn(
        "fixed top-4 right-4 z-toast flex max-h-screen w-auto max-w-toast flex-col gap-3",
        className
      )}
      {...props}
    />
  )
}

const toastVariants = cva(
  "group !pointer-events-auto relative flex w-full items-center rounded-xs border font-inter py-2 px-4 duration-200 data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none",
  {
    variants: {
      variant: {
        default: "bg-yield-positive-bg text-yield-positive-dark border-yield-positive-border",
        destructive:
          "destructive bg-chilli-blush text-chilli-red-dark border-chilli-red-light",
        warning:
          "warning bg-custard-yellow text-brand-yellow-dark border-brand-yellow",
        info:
          "info bg-dinnerplate-white-light text-off-black border-dinnerplate-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>["variant"]>

const toastIconMap: Record<ToastVariant, { icon: typeof SuccessCircleIcon; colorClass: string }> = {
  default: { icon: SuccessCircleIcon, colorClass: "text-yield-positive-dark" },
  destructive: { icon: ErrorCircleIcon, colorClass: "text-chilli-red-dark" },
  warning: { icon: WarningIcon, colorClass: "text-brand-yellow-dark" },
  info: { icon: InfoIcon, colorClass: "text-off-black" },
}

function Toast({
  className,
  variant,
  ...props
}: ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>) {
  return (
    <ToastPrimitives.Root
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  )
}

function ToastAction({ className, ...props }: ComponentPropsWithoutRef<typeof ToastPrimitives.Action>) {
  return (
    <ToastPrimitives.Action
      className={cn(
        "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus-visible:ring-destructive",
        className
      )}
      {...props}
    />
  )
}

function ToastClose({ className, ...props }: ComponentPropsWithoutRef<typeof ToastPrimitives.Close>) {
  return (
    <ToastPrimitives.Close
      className={cn(
        "flex-shrink-0 ml-1 p-2 rounded text-current opacity-60 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-current hover:bg-yield-positive-border/40 group-[.destructive]:hover:bg-chilli-red-light/40 group-[.warning]:hover:bg-butter-yellow group-[.info]:hover:bg-dinnerplate-white transition-[opacity,background-color]",
        className
      )}
      aria-label="Close"
      {...props}
    >
      <CloseIcon className="w-3 h-3" aria-hidden="true" />
    </ToastPrimitives.Close>
  )
}

function ToastTitle({ className, ...props }: ComponentPropsWithoutRef<typeof ToastPrimitives.Title>) {
  return (
    <ToastPrimitives.Title
      className={cn("flex-1", className)}
      {...props}
    />
  )
}

function ToastDescription({ className, ...props }: ComponentPropsWithoutRef<typeof ToastPrimitives.Description>) {
  return (
    <ToastPrimitives.Description
      className={cn("flex-1 min-w-0 text-sm font-semibold whitespace-pre-line", className)}
      {...props}
    />
  )
}

/** Animated progress background that fills the toast from left to right. Pauses on hover. */
function ToastProgressBar({ duration = 10000, onComplete }: { duration?: number; onComplete?: () => void }) {
  const skip = useReducedMotionFallback(duration, onComplete)
  if (skip) return null

  return (
    <div className="absolute inset-0 overflow-hidden rounded-xs">
      <div
        className="h-full bg-yield-positive-border/30 origin-left animate-[progress-fill_linear_forwards] group-hover:[animation-play-state:paused] motion-reduce:animate-none"
        style={{ animationDuration: `${duration}ms` }}
        onAnimationEnd={onComplete}
      />
    </div>
  )
}

/** Thin underline progress bar rendered below the toast content. Pauses on hover. */
function ToastProgressUnderline({ duration = 10000, onComplete }: { duration?: number; onComplete?: () => void }) {
  const skip = useReducedMotionFallback(duration, onComplete)
  if (skip) return null

  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 overflow-hidden rounded-b-xs">
      <div
        className="h-full bg-current opacity-40 origin-left animate-[progress-fill_linear_forwards] group-hover:[animation-play-state:paused] motion-reduce:animate-none"
        style={{ animationDuration: `${duration}ms` }}
        onAnimationEnd={onComplete}
      />
    </div>
  )
}

/** Reusable toast description with an underlined link and a trailing label.
 *
 * Usage:
 *   `<ToastLinkDescription name="Oatside Chocolate" href="/products/1/edit" label="draft saved" />`
 *   renders → [Oatside Chocolate] draft saved
 */
function ToastLinkDescription({ name, href, label, onNavigate }: {
  name: string
  href: string
  label: string
  onNavigate?: () => void
}) {
  return (
    <span className="flex min-w-0 items-center gap-1">
      <a href={href} className={cn("truncate", UNDERLINE_STATIC)} onClick={onNavigate}>
        {name}
      </a>
      <span className="shrink-0">{label}</span>
    </span>
  )
}

type ToastProps = ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  type ToastVariant,
  toastIconMap,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
  ToastProgressBar,
  ToastProgressUnderline,
  ToastLinkDescription,
}
